"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const steps = [{
  title: "Pre-Production",
  description: "We begin with a deep dive into your sonic identity, mapping out the rhythmic structure and melodic foundations that will define your unique sound.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689780/img/studio_session_planning.png"
}, {
  title: "Tracking & Capture",
  description: "Using high-end analog gear and precision microphones, we capture every nuance of the performance with absolute clarity and warmth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689780/img/analog_mixing_console.png"
}, {
  title: "Sonic Sculpting",
  description: "The mixing phase where we balance frequencies and dynamics, applying professional processing to ensure your track hits with maximum impact.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689780/img/audio_waveform_editing.png"
}, {
  title: "Final Mastering",
  description: "The final polish that prepares your music for global distribution, ensuring consistent playback across all systems and streaming platforms.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689780/img/vinyl_mastering_process.png"
}];
function TimelineItem({
  step,
  index
}: {
  step: (typeof steps)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div ref={ref} data-index={index} className={cn("relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 items-center mb-20 md:mb-32 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")}>
      {}
      <div className="absolute left-4 md:left-1/2 top-16 md:top-1/2 w-4 h-4 rounded-md bg-primary -translate-x-1/2 md:-translate-y-1/2 z-10 ring-4 ring-background shadow-md" />

      {}
      <div className={cn("pl-12 md:pl-0 flex justify-start", isEven ? "md:justify-end md:pr-16" : "md:justify-start md:pl-16 md:col-start-2 md:row-start-1")}>
        <img src={step.image} alt={step.title} className="w-32 h-32 md:w-80 md:h-80 rounded-md object-cover shadow-xl border-4 border-border grayscale hover:grayscale-0 transition-all duration-500" />
      </div>

      {}
      <div className={cn("pl-12 md:pl-0", isEven ? "md:pl-16" : "md:pr-16 md:col-start-1 md:row-start-1")}>
        <Card className="border-border shadow-lg bg-card/40 backdrop-blur-md hover:shadow-2xl transition-shadow duration-300 rounded-md">
          <CardHeader>
            <div className="text-sm font-bold text-muted-foreground mb-2 tracking-widest uppercase font-mono">
              Phase 0{index + 1}
            </div>
            <CardTitle className="text-2xl md:text-4xl font-default text-foreground tracking-tighter">
              {step.title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground leading-relaxed text-base md:text-lg font-default">
              {step.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function TheProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="223854" id="the-process" className="py-24 md:py-32 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {}
        <div ref={headerRef} className={cn("text-center max-w-3xl mx-auto mb-20 md:mb-32 transition-all duration-1000 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <h2 className="text-5xl md:text-7xl font-default font-bold text-foreground mb-6 tracking-tighter uppercase">
            The Process
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default max-w-2xl mx-auto">
            From the first beat to the final master, we follow a rigorous sonic workflow designed to elevate your production to industry standards.
          </p>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px border-l-2 border-dashed border-border md:-translate-x-1/2" />

          {}
          <div className="relative z-10 pt-8">
            {steps.map((step, index) => <TimelineItem key={index} step={step} index={index} />)}
          </div>
        </div>
      </div>
    </section>;
}